import { useState, useCallback, useEffect, ReactNode } from 'react';
import { useApi } from '../../api/useApi';
import { PopUpType } from '../../common/components/PopUp';
import { ProtocolSequence, ActiveProtocolSequence } from '../../api/protocolsApi/ProtocolsApi';

export const useProtocolSequences = () => {
  const api = useApi();
  
  const [ activeProtocolSequence, setActiveProtocolSequence ] = useState<ActiveProtocolSequence | null>(null);
  const [ nextProtocolSequence, setNextProtocolSequence ] = useState<ActiveProtocolSequence | null>(null);
  const [ protocolSequences, setProtocolSequences ] = useState<ProtocolSequence[]>([]);

  const [ loading, setLoading ] = useState(false);
  const [ message, setMessage ] = useState<{ type: PopUpType; message: ReactNode } | undefined>(undefined);
  const [ confirmActivateSequence, setConfirmActivateSequence ] = useState<{ year: number; } | null>(null);
  
  const loadProtocolSequences = useCallback(async () => {
    setLoading(true);
    try {
      const protocolSequencesResponse = await api.protocols.getProtocolSequences();
      setProtocolSequences(protocolSequencesResponse.data ?? []);

      const activeProtocolSequenceResponse = await api.protocols.getActiveProtocolSequence();
      setActiveProtocolSequence(activeProtocolSequenceResponse.data ?? null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProtocolSequences();
  }, [loadProtocolSequences]);

  useEffect(() => {
    if (protocolSequences.length > 0) {
      setNextProtocolSequence({
        active_year: Math.max(new Date().getFullYear(), Math.max(...protocolSequences.map(ps => ps.protocol_year))) + 1,
        next_number: 1
      });
    } else {
      setNextProtocolSequence({
        active_year: new Date().getFullYear(),
        next_number: 1
      });
    }
  }, [activeProtocolSequence]);

  const togglePopup = () => {
    setMessage(undefined);
  };

  const activateProtocolSequence = async (year: number, startNumber: number) => {
    setLoading(true);
    try {
      await api.protocols.activateProtocolSequence(year, startNumber);
      loadProtocolSequences();

      setMessage({ type: 'success', message: `Protokolová řada ${year} byla aktivována.` });
    } catch {
      setMessage({ type: 'error', message: 'Při aktivaci číselné řady protokolů došlo k chybě.' });
    } finally {
      setLoading(false);
    }
  };

  const requestConfirmActivateProtocolSequence = (year: number | null) => {
    if(year) {
      setConfirmActivateSequence({ year });
    } else {
      setConfirmActivateSequence(null);
    }
  }

  return {
    protocolSequences,
    activeProtocolSequence,
    nextProtocolSequence,
    activateProtocolSequence,
    requestConfirmActivateProtocolSequence,
    confirmActivateSequence,
    loading,
    message,
    togglePopup
  };
};
